import './bootstrap';

import warning from 'fbjs/lib/warning';
import * as actions from './store/actions/actions';
import { setDebug } from './store/actions/actions';
import AppConstructor from './App/App.jsx';
import { InteractiveSide as Bridge } from '@megogo-interactive/bridge';
import createStore from './store/store';
import ExtensionRegistry from './lib/extension-registry/extension-registry';
import { accessors } from './store/accessors';
import setupStoreListeners from './store/listeners';
import MNS from './services/MNS';
import InteractiveApi from './services/interactive-api';
import { CreativesAPI } from './services/creatives-api';
import interpolate from 'lib/interpolate';
import { pipe } from './lib/pipe';
import { BehaviorSubject, filter, map, partition, share, Subject } from 'rxjs';
import { setExtensionAllowed } from './store/slices/extensions/actions';
import EventsProvider from './providers/events/events-provider';
import CreativeProvider from './providers/creative';
import setupHostAppListeners from './host-app-listeners.js';
import { Kibana } from './utils/trackKibana.js';
import { description as emojiExtension } from 'modules/emoji';
import { readableDate } from '#/utils/formatters.js';
import { RenderersRegistry } from './providers/events/renderers-registry';
import { preloadImages } from '#/utils/preloadAssets.js';

const kibana = new Kibana('root');
kibana.setLevel('log');

const hostGate = (() => {
  try {
    return new Bridge();
  } catch (error) {
    console.error('Bridge creation error', error);
    kibana.fatal(error);
    throw error;
  }
})();

hostGate.onCreate = function (config) {
  try {
    const { logLevel = 'log' } = config;
    const { user_info } = config.mns_config;
    Kibana.setLevel(logLevel);
    Kibana.setContext('user', {
      id: user_info.user_id,
      status: user_info.user_status,
    });
    Kibana.setContext('device', {
      id: user_info.device_id,
      type: user_info.interactive_platform,
      model: user_info.platform,
    });

    createInteractive(hostGate, config);

    const enabledExtensions = config.mns_config.extensions
      .filter(({ allowed }) => allowed)
      .map(({ name, version = 'NA' }) => `${name}@${version}`);

    kibana.log('start', {extensions: enabledExtensions});
  } catch (error) {
    console.error('creating error', error);
    kibana.error(error);

    hostGate.sendNotify.error({
      fatal: true,
      message: error.message,
      code: error.code,
    });
  }
};

/*
 send message "loaded" via bridge to host,
 initiate "create" sequence
*/
hostGate.sendNotify.loaded();

function createInteractive(hostAppGate, passedConfig) {
  const {
    mns_config: {
      user_info,
      /*
        // TODO: extract from msn_config **all** unrelated configuration property to own(s) slices
            api_url,
            video_config_url,
            extensions,
            settings_options,
            */
      cms_url,

      ...mns_config
    },
    override,
    key_codes,
  } = passedConfig;

  mns_config.useSSL = window.location.protocol === 'https:';
  mns_config.port = mns_config.useSSL ? 443 : 80;
  mns_config.userName = 'inter-client';

  mns_config.extensions.forEach((extension) => {
    if (extension.name === 'poll') {
      extension.src ??= '/extensions/poll/latest/index.js';
      extension.options ??= {};
      extension.options.trackEvents ??= true;
      extension.options.dryRun = import.meta.env.DEV ? 'on' : undefined;
    }
  });

  if (import.meta.env.DEV) {
    const extensionsSubstitute = new Map([
      ['poll', import.meta.env.VITE_APP_LOCAL_POLL_EXTENSION],
      ['promo-landing', import.meta.env.VITE_APP_LOCAL_PROMO_EXTENSION],
      ['quiz', import.meta.env.VITE_APP_LOCAL_QUIZ_EXTENSION],
    ]);

    mns_config.extensions.forEach((extension) => {
      const localSource = extensionsSubstitute.get(extension.name);
      if (localSource) {
        extension.src = localSource;
      }
    });
  }

  const config = {
    mns_config: {
      ...mns_config,
    },
    user_info,
    key_codes,
    override,
  };

  const hostAction$ = new Subject();
  const events$ = new Subject();
  const mns = new MNS(mns_config, events$);

  window.Megogo.mnsMessages$ = events$;
  Object.defineProperty(window.Megogo.interactive, 'state', {
    get() {
      return store.getState();
    },
    configurable: true,
  });

  mns.message$ = events$.pipe(
    map((message) =>
      Object.defineProperty(message, 'position', {
        get() {
          return time$.value?.position;
        },
        configurable: true,
      }),
    ),
    share(),
  );

  events$
    .pipe(
      filter(
        (message) =>
          message.topic === 'internal' && message.payload.type === 'error',
      ),
    )
    .subscribe((message) => {
      if (message.payload.code === 401) {
        internalAPI.hostApp.requestNewToken();
      } else if (message.payload.code === 999) {
        // panic
        hostAppGate.sendNotify.error({
          fatal: true,
          message: message.payload.message,
        })
      }
    });

  if (import.meta.env.DEV) {
    hostAction$.subscribe({
      next(message) {
        console.debug(`%c⤋ HOST message`, 'color: red', message);
      },
    });
    const [positionUpdate$, other$] = partition(mns.message$, (message) => {
      return message.topic === '/host-action' && message.payload.type === 'video-position';
    });
    positionUpdate$.subscribe((message) => {
      const obj = message.payload.data;
      let content = `${obj.video_state} ${obj.content_type} at ${readableDate(
        new Date(obj.position * 1e3),
      )}`;
      console.debug(`%c⤋ ${message.topic}`, 'color:red; font-weight:bold', content);
    });
    other$.subscribe((message) => {
      console.log(
        `%c⤋ ${message.topic}`,
        'color:red; font-weight:bold',
        message.payload,
      );
    });
  }

  const time$ = new BehaviorSubject(null);
  events$
    .pipe(
      filter(
        (message) =>
          message.topic === '/host-action' &&
          message.payload.type === 'video-position',
      ),
    )
    .subscribe((message) => {
      time$.next(message.payload.data);
    });

  const creativeProvider = new CreativeProvider();

  const interactiveAPI = new InteractiveApi(mns_config, {
    401() {
      internalAPI.hostApp.requestNewToken();
    },
  });

  const creativesAPI = new CreativesAPI(cms_url, user_info);

  hostAction$
    .pipe(filter(({ event_type }) => event_type === 'settings_changed'))
    .subscribe((event) => {
      store.dispatch(
        setExtensionAllowed({
          name: event.name,
          allowed: event.allowed,
        }),
      );
    });

  hostAction$
    .pipe(filter(({ event_type }) => event_type === 'change_token'))
    .subscribe(({ new_token }) => {
      interactiveAPI.updateToken(new_token);

      interactiveAPI.init().then(({ config }) => {
        /*
            for the moment changing language and login/logout
            without external reloading interactive possible only on STV platform.
            such action could not be complete having player and i9e started.
            In other case need to stop interactive to clear all activity and start it again.
           */
        mns_config.client_id = config.client_id;
        mns_config.token = config.token;

        mns.updateConfig(mns_config);
      });
    });

  const store = createStore(
    {},
    {
      hostAppGate,
      mns,
      interactiveAPI,
      creativesAPI,
      time$,
      hostMessage$: hostAction$,
    },
  );
  mns.onConnectionChange = pipe(
    (isConnected) => ({ isConnected }),
    actions.mnsConnectionStateChange,
    store.dispatch
  );

  setupHostAppListeners(hostAppGate, {
    store,
    events$,
    hostAction$,
  });

  setupStoreListeners(store);

  store.dispatch(actions.interactiveCreate(config));

  /* "public" API */
  window.Megogo.interactive.API ??= {};
  Object.defineProperty(window.Megogo.interactive.API, 'debug', {
    get() {
      return store.getState().app.debug;
    },
    set(enabled) {
      store.dispatch(setDebug(enabled));
    },
  });

  window.Megogo.interactive.API.debug = import.meta.env.DEV;

  /**
   * @name InternalAPI
   * @type {{getExtension(Extension): *, mns: {subscribe: (function(*): function(): Promise<{}>|Promise<unknown>), unsubscribe: ((function(*, {}=): (Promise<{}>))|*), publish: internalAPI.mns.publish, readonly message$: Subject<T>}, hostApp: {removeControl(*): void, requestForeground({onCloseRequest: *, mode?: string}=): void, releaseForeground(): void, readonly message$: Subject<T>, createControl(*): void}, interpolate(*, *): *, getObjectConfig: ((function(*): Promise<*>)|*), readonly timeService: BehaviorSubject<number>}}
   */
  const internalAPI = {
    interactiveAPI,
    creativesAPI,
    assetService: {
      preload: preloadImages
    },
    /**
     *
     * @param {Extension} extension
     * @return {any}
     */
    getExtension(extension) {
      const impl = extensionRegistry.get(extension.name);
      warning(impl, 'no implementation for extension %s', extension.name);

      return impl;
    },
    hostApp: {
      get message$() {
        return hostAction$;
      },
      createControl(controlDescription) {
        const { events = {}, element } = controlDescription;
        const description = {
          // element: sanitizeElementDesc(element),
          element: element,
          events: Object.keys(events),
        };
        hostAppGate.showElement(description);
      },
      removeControl(description) {
        hostAppGate.hideElement(description);
      },
      requestForeground({
        onCloseRequest: _onCloseRequest,
        mode = 'player',
      } = {}) {
        const description = {
          element: {
            element_id: 'fullscreen',
            element_type: 'fullscreen',
            mode,
          },
          events: ['system'],
        };
        /*
        // TODO: reimplement handling back button for mobile version
        {
                    system: (event) => {
                      if (event.event_code === 'back') {
                        onCloseRequest && onCloseRequest();
                      }
                    }
                  }
         */
        hostAppGate.showElement(description);
      },
      releaseForeground() {
        const controlDescription = {
          element: {
            element_id: 'fullscreen',
            element_type: 'fullscreen',
          },
        };
        hostAppGate.hideElement(controlDescription);
      },
      releaseFocus: hostAppGate.releaseFocus,
      sendEvent: hostAppGate.sendEvent,
      trackEvent: hostAppGate.trackEvent,
      requestNewToken() {
        hostAppGate.sendNotify.error({
          fatal: false,
          message: 'Unauthorized',
          code: 401,
        });
      },
    },
    panic(reason) {
      // TODO: send error notify to host app
      // TODO: send error to log.cent.re
      console.error('panic', reason);
    },
    // events$,
    mns: {
      get message$() {
        return mns.message$;
      },
      publish: (topic, message) => {
        mns.publish(topic, {
          ts: time$.value?.position,
          data: message,
        });
      },
      subscribe: (topic) => {
        mns.subscribe(topic);
        return () => mns.unsubscribe(topic);
      },
      unsubscribe: mns.unsubscribe,
    },
    getObjectConfig: interactiveAPI.getConfig,
    interpolate(templateString, parameters) {
      return interpolate(templateString, parameters);
    },
    get timeService() {
      return time$;
    },
  };

  const renderersRegistry = new RenderersRegistry();
  /*
      TODO: resolve API.mns vs. mns
   */
  const extensionRegistry = new ExtensionRegistry({
    store,
    mns,
    API: internalAPI,
    accessors,
    renderersRegistry
  });

  new EventsProvider({
    mns,
    store,
    events$,
    creativeProvider,
    renderersRegistry
  });

  /**
   * @todo: Remove when migrate modules to extension
   * mimic onload extension behaviour
   */
  window.InteractiveExtension['emoji'] = emojiExtension;
  /**
   * вот это 💩 эмулирует загрузку extensions которые сейчас «инлайн» как модули: emoji и poll.
   * отдельной красоты добавляеть src undefined,
   * по которому будет определено какие из расширений надо пометить как загруженые,
   * в данном случае под это правило попадут все расширения без src, т.е. модули
   *
   * TODO: сжечь эту хрень как только перейдем на нормальную загрузку расширений
   *
   */
  store.dispatch({
    type: 'extensions/fetch/fulfilled',
    payload: {
      src: undefined,
    },
  });

  const container = document.getElementById('root');

  AppConstructor(store, internalAPI).render(container);

  if (false === window.__env__.includes('wsw')) {
    window.Megogo.interactive.info();
  }

  /*
  //  What for?
  try {
        const channel = new BroadcastChannel("interactive-channel");
        channel.postMessage(mns_config.token);
      } catch {
        console.log('BroadcastChannel not supported');
      }

   */
}

/*
If you want your app to work offline and load faster, you can change
unregister() to register() below. Note this comes with some pitfalls.
Learn more about service workers: https://bit.ly/CRA-PWA
  // serviceWorker.unregister();
*/
